import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { CastleTier } from './shipTemplates';
import {
  castleTiers,
  hullPoint,
  keelAt,
  railAt,
  railHalfWidthAt,
  tierRailAt,
  type FormedTemplate,
} from './shipForm';
import { CAP_RAIL, RAIL_INSET, castleHalfWidth, type ShipTier } from './hullParts';
import { BRACE, ROPE_RADIUS, rope, type RigStyles } from './rigParts';
import { clothMembrane, sparSolid } from './shipSolids';
import { sideStrips } from './shipTimber';
import { formedMasts, mastRadiusAt, shroudHeadY, type FormedMast } from './shipMastPlan';

/*
 * Shrouds of a formed ship (docs/plans/ships.md 3). A square mast's shrouds come down to a
 * channel outside the hull at the waist rail, far enough aft that a course braced to BRACE.max
 * stays clear of the lee shrouds, and far enough out that every shroud clears the rails and
 * castles it passes. A lateen mast's shrouds come down to its castle's rail (woodcut). The far
 * tier sets up the first and last shroud a side; the mid tier all of them on their channels; the
 * near tier adds deadeyes and ratlines (R2, R4, R29; woodcut).
 */

const SHROUD = { spacing: 0.8, minAft: 0.4, step: 0.05, braceClear: 0.5, stagger: 0.06 } as const;
const CHANNEL = {
  below: 0.35,
  out: 0.45,
  thickness: 0.1,
  overrun: 0.35,
  sink: 0.1,
  lip: 0.12,
} as const;
const STANDOFF = { step: 0.05, most: 2.0 } as const;
/* A lateen mast's shrouds stand close together just abaft it, so the ones on its yard's side
   cross the yard's plane close to the mast, where the yard is still low. */
const RAIL_FOOT = { minAft: 0.4, spacing: 0.6, lift: ROPE_RADIUS + 0.01 } as const;
const DEADEYE = { radius: 0.14, thickness: 0.1, sides: 8, lower: 0.15, upper: 0.6 } as const;
const RATLINE = { spacing: 0.45, first: 0.5, half: 0.02, minGap: 0.25 } as const;
/* A rope's axis keeps this far off the planking, rails and castles it passes. */
const CLEAR = ROPE_RADIUS + 0.05;
const SAMPLES = 60;
const FOOT_LIMIT = 0.97;

/** One mast's shrouds on the starboard side, fore to aft; the port side mirrors them. */
export interface ShroudFan {
  readonly mast: FormedMast;
  readonly onChannel: boolean;
  readonly feet: readonly Vec3[];
  readonly heads: readonly Vec3[];
}

const mirror = ([x, y, z]: Vec3): Vec3 => [x, y, -z];

function lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

function sAt(template: FormedTemplate, x: number): number {
  return x / template.length + 0.5;
}

function castlesOver(template: FormedTemplate, s: number): readonly CastleTier[] {
  return castleTiers(template).filter((tier) => s >= tier.from && s <= tier.to);
}

/* How far out from the centreline the hull reaches at `s` and height `y`: its planking and cap
   rail up to the waist rail, a castle's wall up to its rail; nothing above them. */
export function reachAt(template: FormedTemplate, s: number, y: number): number {
  const rail = railAt(template, s);
  const walls = castlesOver(template, s)
    .filter((tier) => y <= tierRailAt(template, tier, s))
    .map(() => castleHalfWidth(template, s));
  if (y > rail + CAP_RAIL.above) return Math.max(-Infinity, ...walls);
  const keel = keelAt(template, s);
  const u = Math.min(1, Math.max(0, (y - keel) / (rail - keel)));
  const side = Math.max(
    hullPoint(template, s, u)[2],
    railHalfWidthAt(template, s) + CAP_RAIL.overhang
  );
  return Math.max(side, ...walls);
}

/** Whether a rope from `from` to `to` keeps clear of the hull, its rails and castles. */
export function ropeClearsHull(template: FormedTemplate, from: Vec3, to: Vec3): boolean {
  for (let k = 0; k <= SAMPLES; k++) {
    const p = lerp(from, to, k / SAMPLES);
    const s = sAt(template, p[0]);
    if (s < 0 || s > 1) continue;
    if (Math.abs(p[2]) < reachAt(template, s, p[1]) + CLEAR) return false;
  }
  return true;
}

function headsOf(mast: FormedMast, count: number): readonly Vec3[] {
  return Array.from({ length: count }, (_, i): Vec3 => {
    const y = shroudHeadY(mast) - i * SHROUD.stagger;
    return [mast.x, y, mastRadiusAt(mast, y) / 2];
  });
}

/* The nearest point on the channel from which the shroud clears everything on its way up. */
function channelFoot(template: FormedTemplate, x: number, head: Vec3): Vec3 {
  const s = sAt(template, x);
  if (s > FOOT_LIMIT) throw new Error(`${template.type}: shrouds run off the stern at ${s}`);
  const y = railAt(template, s) - CHANNEL.below;
  const base = reachAt(template, s, y) + CHANNEL.out;
  for (let out = 0; out <= STANDOFF.most; out += STANDOFF.step) {
    const foot: Vec3 = [x, y, base + out];
    if (ropeClearsHull(template, foot, head)) return foot;
  }
  throw new Error(`${template.type}: no channel standoff clears the shroud at ${s}`);
}

/* The first shroud stands as far aft as the brace needs: a yard braced to BRACE.max swings its
   lee arm aft tan(BRACE.max) metres for every metre out, and every shroud must lie abaft it. */
function channelFeet(
  template: FormedTemplate,
  mast: FormedMast,
  heads: readonly Vec3[]
): readonly Vec3[] {
  const reach = Math.tan(BRACE.max);
  const clearOf = (foot: Vec3): boolean => foot[0] - mast.x >= reach * foot[2] + SHROUD.braceClear;
  const first = channelFoot(template, mast.x + SHROUD.minAft, heads[0]!);
  let aft = Math.max(SHROUD.minAft, reach * first[2] + SHROUD.braceClear);
  for (;;) {
    const at = aft;
    const feet = heads.map((head, i) =>
      channelFoot(template, mast.x + at + i * SHROUD.spacing, head)
    );
    if (feet.every(clearOf)) return feet;
    aft += SHROUD.step;
  }
}

/* On a castle's rail, just clear of its top, where the far tier's closed castle ends too. */
function railFoot(template: FormedTemplate, x: number): Vec3 {
  const s = sAt(template, x);
  const over = castlesOver(template, s);
  if (over.length === 0) throw new Error(`${template.type}: no castle rail for shrouds at ${s}`);
  const rail = Math.max(...over.map((tier) => tierRailAt(template, tier, s)));
  return [x, rail + RAIL_FOOT.lift, castleHalfWidth(template, s) - RAIL_INSET];
}

function fanOf(template: FormedTemplate, mast: FormedMast): ShroudFan {
  const heads = headsOf(mast, mast.config.shrouds ?? 2);
  if (mast.config.rig === 'lateen') {
    const feet = heads.map((_, i) =>
      railFoot(template, mast.x + RAIL_FOOT.minAft + i * RAIL_FOOT.spacing)
    );
    return { mast, onChannel: false, feet, heads };
  }
  return { mast, onChannel: true, feet: channelFeet(template, mast, heads), heads };
}

const fans = new WeakMap<FormedTemplate, readonly ShroudFan[]>();

/** Every mast's shrouds, worked out once per template. */
export function shroudFans(template: FormedTemplate): readonly ShroudFan[] {
  const known = fans.get(template);
  if (known) return known;
  const made = formedMasts(template).map((mast) => fanOf(template, mast));
  fans.set(template, made);
  return made;
}

/* The far tier keeps the first and last shroud a side, so its fan spans what the others do. */
function tierFan(fan: ShroudFan, tier: ShipTier): ShroudFan {
  if (tier !== 'far' || fan.feet.length <= 2) return fan;
  const ends = [0, fan.feet.length - 1];
  return { ...fan, feet: ends.map((i) => fan.feet[i]!), heads: ends.map((i) => fan.heads[i]!) };
}

function buildChannel(
  builder: MeshBuilder,
  template: FormedTemplate,
  style: VertexStyle,
  fan: ShroudFan
): void {
  const xs = fan.feet.map((foot) => foot[0]);
  const from = sAt(template, Math.min(...xs) - CHANNEL.overrun);
  const to = sAt(template, Math.max(...xs) + CHANNEL.overrun);
  const outer = Math.max(...fan.feet.map((foot) => foot[2])) + CHANNEL.lip;
  const samples = [from, ...xs.map((x) => sAt(template, x)), to];
  sideStrips(builder, style, samples, (s) => {
    const x = (s - 0.5) * template.length;
    const top = railAt(template, s) - CHANNEL.below;
    const inner = reachAt(template, s, top) - CHANNEL.sink;
    const bottom = top - CHANNEL.thickness;
    return [
      [x, bottom, outer],
      [x, top, outer],
      [x, top, inner],
      [x, bottom, inner],
    ];
  });
}

function unit(v: Vec3): Vec3 {
  const n = Math.hypot(v[0], v[1], v[2]);
  return [v[0] / n, v[1] / n, v[2] / n];
}

/* A pair of deadeyes on each shroud just above its foot, their faces turned outboard. */
function buildDeadeyes(builder: MeshBuilder, style: VertexStyle, foot: Vec3, head: Vec3): void {
  const along = unit([head[0] - foot[0], head[1] - foot[1], head[2] - foot[2]]);
  const axis = unit([0, along[2], -along[1]]);
  for (const at of [DEADEYE.lower, DEADEYE.upper]) {
    const c: Vec3 = [foot[0] + along[0] * at, foot[1] + along[1] * at, foot[2] + along[2] * at];
    const half = DEADEYE.thickness / 2;
    sparSolid(builder, style, {
      from: [c[0] - axis[0] * half, c[1] - axis[1] * half, c[2] - axis[2] * half],
      to: [c[0] + axis[0] * half, c[1] + axis[1] * half, c[2] + axis[2] * half],
      radiusFrom: DEADEYE.radius,
      radiusTo: DEADEYE.radius,
      sides: DEADEYE.sides,
      painterWidthScale: 1,
    });
  }
}

function atHeight(foot: Vec3, head: Vec3, y: number): Vec3 {
  return lerp(foot, head, (y - foot[1]) / (head[1] - foot[1]));
}

/* Ratlines cross each gap between neighbouring shrouds every 0.45 m, up to where the shrouds
   close in under the top. Each is a narrow band of cloth from one shroud's axis to the next. */
function buildRatlines(
  builder: MeshBuilder,
  style: VertexStyle,
  feet: readonly Vec3[],
  heads: readonly Vec3[]
): void {
  for (let i = 0; i + 1 < feet.length; i++) {
    const [f0, f1, h0, h1] = [feet[i]!, feet[i + 1]!, heads[i]!, heads[i + 1]!];
    const top = Math.min(h0[1], h1[1]);
    for (let y = Math.max(f0[1], f1[1]) + RATLINE.first; y < top; y += RATLINE.spacing) {
      const [a, b] = [atHeight(f0, h0, y), atHeight(f1, h1, y)];
      if (Math.hypot(b[0] - a[0], b[2] - a[2]) < RATLINE.minGap) break;
      const up = (p: Vec3, d: number): Vec3 => [p[0], p[1] + d, p[2]];
      clothMembrane(builder, style, [
        [up(a, RATLINE.half), up(b, RATLINE.half)],
        [up(a, -RATLINE.half), up(b, -RATLINE.half)],
      ]);
    }
  }
}

function buildSide(
  builder: MeshBuilder,
  styles: RigStyles,
  fan: ShroudFan,
  tier: ShipTier,
  side: number
): void {
  const place = (p: Vec3): Vec3 => (side > 0 ? p : mirror(p));
  const feet = fan.feet.map(place);
  const heads = fan.heads.map(place);
  feet.forEach((foot, i) => {
    rope(builder, styles.rope, foot, heads[i]!);
  });
  if (tier !== 'near') return;
  feet.forEach((foot, i) => {
    buildDeadeyes(builder, styles.spar, foot, heads[i]!);
  });
  buildRatlines(builder, styles.rope, feet, heads);
}

export function buildShrouds(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  styles: RigStyles
): void {
  for (const whole of shroudFans(template)) {
    const fan = tierFan(whole, tier);
    if (tier !== 'far' && fan.onChannel) buildChannel(builder, template, styles.spar, fan);
    for (const side of [1, -1]) buildSide(builder, styles, fan, tier, side);
  }
}
